import React from "react";
import { Box, Button, Group, Text, Stack } from "@mantine/core";
import { IconBrandWindows, IconBrandApple, IconDownload } from "@tabler/icons-react";
import { getCurrentEnvironment, Environment } from "@/common/modals.tsx";
import classes from "./SoftwarePanel.module.css";

export interface SoftwarePlatform {
  os: "Windows" | "macOS" | "Linux";
  downloadUrl: string;
}

export interface SoftwarePackage {
  name: string;
  version: string;
  platforms: SoftwarePlatform[];
}

// Environment-specific base URLs for software downloads
const SOFTWARE_BASE_URLS: Record<Environment, string> = {
  Local: "http://localhost:8000/api/software/download/ndc",
  Test: "https://neurotune.test.bios.digital/api/software/download/ndc/",
  UK: "https://neurotune.uk.bios.digital/api/software/download/ndc/",
  USA: "https://neurotune.us.bios.digital/api/software/download/ndc/",
};

// Software catalog - hardcoded packages with environment-specific URLs
const SOFTWARE_CATALOG: SoftwarePackage[] = [
  {
    name: "Neurotune Data Collector",
    version: "4.0.0",
    platforms: [
      {
        os: "Windows",
        downloadUrl: `${SOFTWARE_BASE_URLS.Local}/x64`,
      },
      {
        os: "macOS",
        downloadUrl: `${SOFTWARE_BASE_URLS.Local}/aarch64`,
      },
    ],
  },
];

// Build catalog with environment-specific URLs
export const buildSoftwareCatalog = (): SoftwarePackage[] => {
  const env = getCurrentEnvironment();
  const baseUrl = SOFTWARE_BASE_URLS[env];

  return SOFTWARE_CATALOG.map((pkg) => ({
    ...pkg,
    platforms: pkg.platforms.map((platform) => ({
      ...platform,
      downloadUrl: platform.downloadUrl.replace(SOFTWARE_BASE_URLS.Local, baseUrl),
    })),
  }));
};

const getOsIcon = (os: "Windows" | "macOS" | "Linux") => {
  switch (os) {
    case "Windows":
      return <IconBrandWindows size={20} />;
    case "macOS":
      return <IconBrandApple size={20} />;
    case "Linux":
      return <Text size="sm" fw={700}>L</Text>;
  }
};

export const triggerDownload = (url: string) => {
  const link = document.createElement("a");
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export interface SoftwarePanelProps { }

export const SoftwarePanel: React.FC<SoftwarePanelProps> = () => {
  const softwareCatalog = buildSoftwareCatalog();

  return (
    <Box className={classes.root}>
      <Stack gap="md">
        {softwareCatalog.map((pkg) => (
          <Box key={pkg.name} className={classes.card}>
            <Box className={classes.cardHeader}>
              <Text fw={600} size="lg">
                {pkg.name}
              </Text>
              <Text size="sm" c="dimmed">
                Version {pkg.version}
              </Text>
            </Box>
            <Box className={classes.cardContent}>
              <Group gap="sm">
                {pkg.platforms.map((platform) => (
                  <Button
                    key={platform.os}
                    size="sm"
                    variant="light"
                    leftSection={getOsIcon(platform.os)}
                    rightSection={<IconDownload size={16} />}
                    onClick={() => triggerDownload(platform.downloadUrl)}
                  >
                    {platform.os}
                  </Button>
                ))}
              </Group>
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
